import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Icon from '../../Icon';

function AdminSMSView({ user }) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/auth/users');
        const allUsers = Array.isArray(res?.data?.users) ? res.data.users : [];
        setTenants(allUsers);
        setFilteredTenants(allUsers);
      } catch (e) {
        console.error('Failed to fetch users', e);
        showNotification('error', 'Failed to load users');
      }
    };
    fetchTenants();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTenants(tenants);
      return;
    }
    const q = searchQuery.toLowerCase();
    const filtered = tenants.filter(t => 
      (t.name || '').toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.phone || '').toLowerCase().includes(q) ||
      (t.idNumber || '').toLowerCase().includes(q)
    );
    setFilteredTenants(filtered);
  }, [searchQuery, tenants]);

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
    setPhoneNumber(tenant.phone || '');
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const showNotification = (type, msg) => {
    setNotification({ type, message: msg });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSendSMS = async () => {
    if (!selectedTenant) {
      showNotification('error', 'Please select a user');
      return;
    }
    if (!phoneNumber.trim()) {
      showNotification('error', 'Phone number is required');
      return;
    }
    if (!message.trim()) {
      showNotification('error', 'Message cannot be empty');
      return;
    }

    try {
      setSending(true);
      await api.post('/frp/admin/send-sms', {
        to: phoneNumber,
        text: message,
        tenantId: selectedTenant._id
      });
      showNotification('success', `SMS sent successfully to ${selectedTenant.name}`);
      setMessage('');
      setSelectedTenant(null);
      setPhoneNumber('');
    } catch (e) {
      console.error('Failed to send SMS', e);
      showNotification('error', e?.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const smsCount = message.length > 0 ? Math.ceil(message.length / 160) : 0;

  return (
    <div className="min-h-screen font-sans pb-8 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1a1625 0%, #2d1f47 50%, #3d2563 100%)'
    }}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating orbs */}
        <div className="absolute top-20 right-20 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute bottom-40 left-20 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '7s', animationDelay: '2s' }} />
        <div className="absolute top-1/3 right-1/3 w-72 h-72 bg-purple-500/8 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        
        {/* Dots pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-6 right-6 z-50" style={{ animation: 'slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border ${
            notification.type === 'success' 
              ? 'bg-emerald-500/90 border-emerald-400/30 text-white' 
              : 'bg-rose-500/90 border-rose-400/30 text-white'
          }`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium pr-2">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-auto w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/admin')}
              className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/70 hover:text-white"
            >
              <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => navigate('/admin/sms-history')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 hover:border-violet-500/50 transition-all text-violet-300 hover:text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">History</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-2xl shadow-violet-500/30 mb-6 relative">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            {/* Message bubbles animation */}
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-fuchsia-400 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDuration: '2s' }}>
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Send SMS
          </h1>
          <p className="text-purple-200/70 text-base sm:text-lg max-w-xl mx-auto">
            Instantly reach your users with text messages
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl overflow-hidden" style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255,255,255,0.1)'
        }}>
          {/* Card Header Accent */}
          <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500" />
          
          <div className="p-6 sm:p-8 lg:p-10 space-y-8">
            {/* User Selection */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs">1</span>
                Select Recipient
              </label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl transition-all text-left ${
                    dropdownOpen 
                      ? 'bg-white/10 border-violet-500/50 ring-2 ring-violet-500/20' 
                      : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                  } border`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {selectedTenant ? (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold shadow-lg shadow-violet-500/20 flex-shrink-0">
                          {getInitials(selectedTenant.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-semibold text-white truncate">{selectedTenant.name}</p>
                          <p className="text-sm text-purple-300/70 truncate">{selectedTenant.phone || selectedTenant.email}</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20">
                          <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                          <span className="text-xs font-medium text-violet-400">Selected</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-purple-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span className="text-purple-300/50">Choose a user to message...</span>
                      </>
                    )}
                  </div>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${dropdownOpen ? 'bg-violet-500/20 rotate-180' : 'bg-white/5'}`}>
                    <svg className="w-4 h-4 text-purple-300/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-3 rounded-2xl overflow-hidden" style={{
                    background: 'linear-gradient(180deg, rgba(45,31,71,0.98) 0%, rgba(26,22,37,0.98) 100%)',
                    backdropFilter: 'blur(40px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
                  }}>
                    <div className="p-4 border-b border-white/10">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-purple-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search by name, email, or phone..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 text-sm bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 text-white placeholder-purple-300/40 transition-all"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {filteredTenants.length === 0 ? (
                        <div className="p-8 text-center">
                          <div className="w-16 h-16 rounded-2xl bg-purple-900/50 flex items-center justify-center mx-auto mb-3">
                            <svg className="w-8 h-8 text-purple-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <p className="text-sm text-purple-300/50">No users found</p>
                        </div>
                      ) : (
                        filteredTenants.map((tenant, idx) => (
                          <button
                            key={tenant._id}
                            onClick={() => handleSelectTenant(tenant)}
                            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/5 transition-all text-left group"
                            style={{ animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both` }}
                          >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-700 to-purple-900 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 group-hover:from-violet-500 group-hover:to-fuchsia-500 transition-all shadow-lg">
                              {getInitials(tenant.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-white truncate group-hover:text-violet-300 transition-colors">{tenant.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {tenant.phone && (
                                  <span className="text-xs text-fuchsia-400 font-medium">{tenant.phone}</span>
                                )}
                                {tenant.email && !tenant.phone && (
                                  <span className="text-xs text-purple-300/50 truncate">{tenant.email}</span>
                                )}
                              </div>
                            </div>
                            {tenant.phone && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                <span className="text-xs text-emerald-400">Phone</span>
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedTenant && !selectedTenant.phone && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm text-amber-300">This user has no phone number on record</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            {/* Phone Number */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs">2</span>
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-purple-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="0712345678 or 254712345678"
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 text-white placeholder-purple-300/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!selectedTenant}
                />
              </div>
              <p className="text-xs text-purple-300/40 pl-1">
                Kenyan numbers (07, 01, +254) are normalized automatically
              </p>
            </div>

            {/* Message */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs">3</span>
                Message
              </label>
              <div className="relative">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your SMS message here..."
                  rows={5}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 text-white placeholder-purple-300/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed resize-none"
                  disabled={!selectedTenant}
                  style={{ lineHeight: '1.7' }}
                />
              </div>
              
              {/* SMS Counter */}
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-mono px-2.5 py-1 rounded-lg ${
                    message.length > 160 ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-purple-300/60'
                  }`}>
                    {message.length}/160
                  </span>
                  {smsCount > 0 && (
                    <span className={`text-xs px-2.5 py-1 rounded-lg ${
                      smsCount > 1 ? 'bg-fuchsia-500/20 text-fuchsia-400' : 'bg-violet-500/20 text-violet-400'
                    }`}>
                      {smsCount} SMS{smsCount > 1 ? ' messages' : ''}
                    </span>
                  )}
                </div>
                {message.length > 160 && (
                  <span className="text-xs text-amber-400/70">
                    Split into {smsCount} messages
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleSendSMS}
                disabled={!selectedTenant || !phoneNumber.trim() || !message.trim() || sending}
                className="flex-1 relative group overflow-hidden rounded-2xl disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-transform group-hover:scale-105 group-disabled:scale-100" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
                  background: 'linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.1) 50%, transparent 75%)',
                  backgroundSize: '200% 200%',
                  animation: 'shimmer 2s infinite'
                }} />
                <div className="relative flex items-center justify-center gap-3 px-8 py-4 text-white font-semibold">
                  {sending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Send SMS</span>
                    </>
                  )}
                </div>
              </button>
              <button
                onClick={() => {
                  setSelectedTenant(null);
                  setPhoneNumber('');
                  setMessage('');
                }}
                disabled={sending}
                className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-purple-200 hover:text-white rounded-2xl transition-all font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-8 rounded-2xl p-5 relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(168,85,247,0.1) 100%)',
          border: '1px solid rgba(139,92,246,0.2)'
        }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="relative flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Quick Tips</h4>
              <ul className="text-xs text-purple-200/60 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-violet-400 mt-0.5">•</span>
                  Messages over 160 characters are sent as multiple SMS
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-400 mt-0.5">•</span>
                  Phone numbers are auto-formatted for Kenya
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-400 mt-0.5">•</span>
                  View all sent messages in the History section
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        /* Custom scrollbar */
        .max-h-80::-webkit-scrollbar {
          width: 6px;
        }
        .max-h-80::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 3px;
        }
        .max-h-80::-webkit-scrollbar-thumb {
          background: rgba(139,92,246,0.3);
          border-radius: 3px;
        }
        .max-h-80::-webkit-scrollbar-thumb:hover {
          background: rgba(139,92,246,0.5);
        }
      `}</style>
    </div>
  );
}

export default AdminSMSView;
